import EyeHide from 'assets/auth/password-eye-hidden.svg?react';
import EyeShow from 'assets/auth/password-eye.svg?react';
import clsx from 'clsx';
import React, { KeyboardEventHandler, Ref, useMemo, useState } from 'react';

import styles from './Input.module.scss';

export type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  containerRef?: Ref<HTMLDivElement>;
  onEnterPressed?: (text: string) => void;
  className?: string;
  containerClassName?: string;
  errorText?: string;
  helperText?: string;
  type?: string;
  badge?: React.ReactNode;
  /*
   * endAdornment is an element that is placed on the right of the Input (visually inside the input)
   */
  endAdornment?: React.ReactNode;
  endAdornmentClassname?: string;
  testId?: string;
  /*
   * 'normal' - input will have minimal width
   * 'formInput' - input will have the standard width (as all form elements)
   */
  variant?: 'normal' | 'formInput';

  /*
   * This prop might be set if input is part of Autocomplete (DropDown/Combobox/etc).
   * If we don't do this, `selectedValue` ends up as DOM attribute which isn't what we want.
   * It might be better implemented by wrapping inputs that are used in Autocomplete, but it's less of a hassle like it is now.
   */
  selectedValue?: unknown;
  /*
   * These two props placed on the left and on the right from the input
   */
  leftHelperText?: string;
  rightHelperText?: string;
  bottomHelperText?: string;
  topHelperText?: string;
  topRightHelperText?: string;
  fullWidth?: boolean;
  isSmallInput?: boolean;
  errorTestId?: string;
};

export const Input = React.forwardRef<HTMLInputElement, Props>(function Input(
  props,
  ref,
) {
  const {
    containerRef,
    onClick,
    style,
    className,
    containerClassName,
    helperText,
    errorText,
    onEnterPressed,
    type,
    badge,
    endAdornment: endAdornmentProps,
    endAdornmentClassname,
    variant,
    testId,
    selectedValue,
    leftHelperText,
    rightHelperText,
    bottomHelperText,
    topHelperText,
    topRightHelperText,
    isSmallInput,
    disabled,
    readOnly,
    fullWidth,
    errorTestId,
    ...rest
  } = props;
  const isError = !!errorText;

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      if (onEnterPressed) {
        onEnterPressed(e.currentTarget.value);
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };
  const [showPassword, setShowPassword] = useState<boolean | undefined>(
    type === 'password' ? false : undefined,
  );

  const endAdornment = useMemo(() => {
    if (showPassword === undefined) {
      if (!props.endAdornment) return undefined;
      return { element: props.endAdornment, onClick: props.onClick };
    }
    return {
      element: showPassword ? <EyeHide /> : <EyeShow />,
      onClick: () => setShowPassword((prevState) => !prevState),
    };
  }, [props.endAdornment, showPassword]);

  return (
    <div
      data-test-id={testId}
      ref={containerRef}
      onClick={onClick}
      style={style}
      className={clsx(
        styles.inputContainer,
        fullWidth && styles.fullWidth,
        containerClassName,
      )}
    >
      <div className={clsx(styles.rowContainer, disabled && styles.opacity_50)}>
        {leftHelperText && (
          <div className={styles.inputHelperText}>{leftHelperText}</div>
        )}
        <div
          className={clsx(
            styles.columnContainer,
            fullWidth && styles.fullWidth,
          )}
        >
          {(topHelperText || topRightHelperText) && (
            <div
              style={{
                justifyContent:
                  !topHelperText && topRightHelperText
                    ? 'flex-end'
                    : 'space-between',
              }}
              className={clsx(styles.rowContainer, styles.absoluteTopContainer)}
            >
              {topHelperText && (
                <div className={styles.inputHelperText}>{topHelperText}</div>
              )}
              {topRightHelperText && (
                <div className={styles.inputHelperTextGrey}>
                  {topRightHelperText}
                </div>
              )}
            </div>
          )}
          <input
            ref={ref}
            className={clsx(
              styles.input,
              endAdornment ? styles.inputWithAdornment : null,
              variant === 'normal' ? styles.nonFormInput : null,
              readOnly && styles.readOnly,
              isSmallInput && styles.smallInput,
              className,
              disabled && styles.disabled,
            )}
            disabled={disabled}
            readOnly={readOnly}
            data-error={isError.toString()}
            onKeyDown={onEnterPressed ? onKeyDown : undefined}
            type={showPassword ? 'input' : type}
            {...rest}
          />
          {endAdornment && (
            <div
              className={clsx(
                styles.endAdornment,
                type === 'password' && styles.passwordEye,
                endAdornmentClassname,
                errorText,
              )}
              onClick={endAdornment.onClick}
              onFocus={props.onFocus}
              onMouseDown={props.onMouseDown}
            >
              {endAdornment.element}
            </div>
          )}
          <div
            data-test-id={errorTestId}
            className={clsx(
              styles.absoluteBottomContainer,
              styles.inputHelperText,
              errorText && styles.inputErrorText,
            )}
          >
            {errorText ? errorText : bottomHelperText}
          </div>
        </div>
        {rightHelperText && (
          <div className={styles.inputHelperText}>{rightHelperText}</div>
        )}
      </div>
      {badge}
    </div>
  );
});
